import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type { ConsolidationCurrencyRow, ConsolidationGap, FinanceConsolidation } from "@expertloop/shared";

/**
 * Explication de chaque lacune, en une phrase que le lecteur comprend seul.
 *
 * Une lacune sans phrase serait un code technique affiché à un humain ; c'est
 * précisément ce que cette section existe pour éviter.
 */
const GAP_LABELS: Record<ConsolidationGap, MessageKey> = {
	subscription_ledger_empty: "adminFinance.gapSubscriptionLedgerEmpty",
	period_precedes_subscription_ledger: "adminFinance.gapPeriodPrecedesSubscriptionLedger",
	ai_ledger_empty: "adminFinance.gapAiLedgerEmpty",
	period_precedes_ai_ledger: "adminFinance.gapPeriodPrecedesAiLedger",
	ai_cost_unpriced: "adminFinance.gapAiCostUnpriced",
	ai_cost_other_currency: "adminFinance.gapAiCostOtherCurrency",
};

/**
 * Contribution suivie de la plateforme.
 *
 * Le résultat n'apparaît que lorsque aucune lacune ne l'empêche. Sinon, la
 * place du chiffre est prise par les raisons de son absence : un zéro se
 * lirait comme « la plateforme n'a rien gagné », ce qui serait faux.
 */
export function ContributionSection({ consolidation }: { consolidation: FinanceConsolidation }) {
	const { t } = useI18n();

	if (consolidation.byCurrency.length === 0) {
		return <p className="admin-overview__state">{t("adminOverview.empty")}</p>;
	}

	return (
		<>
			<p className="admin-finance__scope">{t("adminFinance.contributionIntro")}</p>

			<p className="admin-finance__formula">
				<strong>{t("adminFinance.contributionFormula")} : </strong>
				{consolidation.formula}
			</p>

			<div className="admin-overview__currencies">
				{consolidation.byCurrency.map((row) => (
					<CurrencyContribution key={row.currency} row={row} />
				))}
			</div>

			<details className="admin-overview__limitations">
				<summary>{t("adminFinance.contributionExclusions")}</summary>
				<ul>
					{consolidation.exclusions.map((exclusion) => (
						<li key={exclusion}>{exclusion}</li>
					))}
				</ul>
			</details>

			<dl className="admin-finance__reliability">
				<dt>{t("adminFinance.reliabilitySubscriptions")}</dt>
				<dd>{formatStart(consolidation.reliability.subscriptionLedgerStartedAt, t)}</dd>
				<dt>{t("adminFinance.reliabilityAi")}</dt>
				<dd>{formatStart(consolidation.reliability.aiLedgerStartedAt, t)}</dd>
			</dl>
		</>
	);
}

function formatStart(startedAt: string | null, t: (key: MessageKey) => string): string {
	return startedAt ? new Date(startedAt).toLocaleDateString() : t("adminFinance.reliabilityNever");
}

function CurrencyContribution({ row }: { row: ConsolidationCurrencyRow }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	return (
		<div className="admin-overview__currency">
			<h3 className="admin-overview__currency-title">{row.currency.toUpperCase()}</h3>

			{row.trackedContribution === null ? (
				<p className="admin-overview__state admin-overview__state--muted">
					{t("adminFinance.contributionUnavailable")}
				</p>
			) : (
				<div className="admin-overview__tiles">
					<div className="admin-overview__tile">
						<span className="admin-overview__tile-label">{t("adminFinance.contributionValue")}</span>
						<strong className="admin-overview__tile-value">
							{formatCents(row.trackedContribution, row.currency)}
						</strong>
					</div>
				</div>
			)}

			<ul className="admin-finance__components">
				<li>
					<span>{t("adminFinance.componentPlatformFee")}</span>
					<span>+ {formatCents(row.marketplacePlatformFee, row.currency)}</span>
				</li>
				<li>
					<span>{t("adminFinance.componentSubscriptions")}</span>
					<span>+ {formatCents(row.subscriptionRevenue, row.currency)}</span>
				</li>
				<li>
					<span>{t("adminFinance.componentFeeReturned")}</span>
					<span>− {formatCents(row.platformFeeReturned, row.currency)}</span>
				</li>
				<li>
					<span>{t("adminFinance.componentStripeFee")}</span>
					<span>− {formatCents(row.marketplaceStripeFee, row.currency)}</span>
				</li>
				<li>
					<span>{t("adminFinance.componentAiCost")}</span>
					<span>− {formatCents(row.aiCostKnown, row.currency)}</span>
				</li>
			</ul>

			{row.gaps.length > 0 && (
				<ul className="admin-finance__gaps">
					{row.gaps.map((gap) => (
						<li key={gap}>
							{gap === "ai_cost_unpriced"
								? t(GAP_LABELS[gap], {
									count: row.aiInvocationsUnpriced,
									plural: row.aiInvocationsUnpriced !== 1 ? "s" : "",
								})
								: t(GAP_LABELS[gap])}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
